import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { SupplierV1Service } from '../../../../api/services/supplier-v-1.service';
import {
  loadSupplierList,
  readAllSupplierListFailed,
  readAllSupplierListSuccess,
} from '../../actions/supplier-list/supplier-list.actions';
import { DynamicFilter } from '../../../../api/models/dynamic-filter';
import { PaginationWithFiltersRequest } from '../../../../api/models/pagination-with-filters-request';

@Injectable()
export class SupplierListEffects {
  constructor(private readonly actions$: Actions, private readonly supplierV1Service: SupplierV1Service) {}

  loadSupplierList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSupplierList),
      switchMap(({ pagination }) => {
        const mappedFilters: DynamicFilter[] = (pagination.filters ?? []).map((f) => {
          if (f.field === 'status') {
            const statusValue = f.value?.[0];
            return {
              ...f,
              field: 'active',
              operator: 'IS_EQUAL' as const,
              value: [statusValue === 'Active'],
            };
          }
          return {
            ...f,
            field: this.mapFieldName(f.field),
          };
        });

        // When sorting by status (boolean 'active' field), invert direction
        // so that ASC shows Active first (like alphabetical)
        const mappedSortDirection = this.mapSortDirection(pagination.sortBy, pagination.sortDirection);

        const mappedRequest: PaginationWithFiltersRequest = {
          ...pagination,
          sortBy: this.mapFieldName(pagination.sortBy),
          sortDirection: mappedSortDirection,
          filters: mappedFilters,
        };

        return this.supplierV1Service.listSuppliersV1({ body: mappedRequest }).pipe(
          // NOSONAR
          map((resp) => {
            const suppliers = (resp.content ?? []).map((supplier: any) => ({
              ...supplier,
              status: supplier.active ? 'Active' : 'Deactivated',
            }));

            return readAllSupplierListSuccess({
              suppliers,
              totalElements: resp.totalElements ?? 0,
            });
          }),
          catchError((error) => of(readAllSupplierListFailed({ error })))
        );
      })
    )
  );

  private mapFieldName(fieldName: string | undefined): string | undefined {
    switch (fieldName) {
      case 'slmSupplierNumber':
        return 'supplierNumber';
      case 'supplierNumber':
        return 'erpReference';
      case 'status':
        return 'active';
      default:
        return fieldName;
    }
  }

  private mapSortDirection(
    sortBy: string | undefined,
    sortDirection: 'ASC' | 'DESC' | undefined
  ): 'ASC' | 'DESC' | undefined {
    if (sortBy !== 'status' || !sortDirection) {
      return sortDirection;
    }
    return sortDirection === 'ASC' ? 'DESC' : 'ASC';
  }
}
